import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { supabase } from "../supabaseClient";

interface ShoppingItem {
  id: string;
  name: string;
  label: string;
  category: string;
}

interface ShoppingListContextType {
  shoppingItems: ShoppingItem[];
  setShoppingItems: React.Dispatch<React.SetStateAction<ShoppingItem[]>>;
}

const ShoppingListContext = createContext<ShoppingListContextType | undefined>(
  undefined
);

export const ShoppingListProvider = ({ children }: { children: ReactNode }) => {
  const [shoppingItems, setShoppingItems] = useState<ShoppingItem[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // 🔁 Hae käyttäjä ja ostoslista
  useEffect(() => {
    const fetchShoppingList = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      setUserId(user.id);

      const { data, error } = await supabase
        .from("shopping_list_items")
        .select("id, name, label, category")
        .eq("user_id", user.id);

      if (error) {
        console.error("Virhe ostoslistan haussa:", error);
      } else {
        setShoppingItems(data as ShoppingItem[]);
        setInitialLoadComplete(true);
      }
    };

    fetchShoppingList();
  }, []);

  // 💾 Synkronoi uudet lisäykset Supabaseen
  useEffect(() => {
    const syncToSupabase = async () => {
      if (!userId || !initialLoadComplete) return;

      const { data: existing } = await supabase
        .from("shopping_list_items")
        .select("name")
        .eq("user_id", userId);

      const existingNames =
        existing?.map((item) => item.name.trim().toLowerCase()) || [];

      const newItems = shoppingItems.filter(
        (item) => !existingNames.includes(item.name.trim().toLowerCase())
      );

      if (newItems.length > 0) {
        const inserts = newItems.map((item) => ({
          user_id: userId,
          name: item.name,
          label: item.label,
          category: item.category,
        }));

        const { error } = await supabase
          .from("shopping_list_items")
          .insert(inserts);

        if (error) {
          console.error("Virhe tallennettaessa Supabaseen:", error);
        }
      }
    };

    syncToSupabase();
  }, [shoppingItems, userId, initialLoadComplete]);

  return (
    <ShoppingListContext.Provider value={{ shoppingItems, setShoppingItems }}>
      {children}
    </ShoppingListContext.Provider>
  );
};

export const useShoppingList = (): ShoppingListContextType => {
  const context = useContext(ShoppingListContext);
  if (!context) {
    throw new Error(
      "useShoppingList must be used within a ShoppingListProvider"
    );
  }
  return context;
};
